import React, { useState } from 'react'
import { Check, Send } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { ChoiceQuestion } from '../types'

interface ChoiceCardProps {
  questions: ChoiceQuestion[]
  /** False once the conversation has moved on — the card then shows read-only. */
  active: boolean
  onSubmit: (answer: string) => void
}

// One line per question — "Recipients: CTOs at US SaaS companies" — sent as the
// user's ordinary next message, so the model reads it like anything they'd typed.
// The model sometimes reuses one header for every question, which would make the
// lines indistinguishable — those fall back to the full question text.
function composeAnswer(questions: ChoiceQuestion[], picks: string[][]): string {
  const headerCounts = new Map<string, number>()
  for (const q of questions) {
    const key = q.header.trim().toLowerCase()
    headerCounts.set(key, (headerCounts.get(key) ?? 0) + 1)
  }
  return questions
    .map((q, i) => {
      const label = headerCounts.get(q.header.trim().toLowerCase()) === 1 ? q.header : q.question
      return `${label}: ${picks[i]!.join(', ')}`
    })
    .join('\n')
}

export const ChoiceCard: React.FC<ChoiceCardProps> = ({ questions, active, onSubmit }) => {
  const { currentTheme: t } = useTheme()
  const [selected, setSelected] = useState<string[][]>(() => questions.map(() => []))
  const [otherOpen, setOtherOpen] = useState<boolean[]>(() => questions.map(() => false))
  const [otherText, setOtherText] = useState<string[]>(() => questions.map(() => ''))
  const [submitted, setSubmitted] = useState(false)

  const locked = !active || submitted

  const update = <T,>(list: T[], index: number, value: T): T[] => list.map((v, i) => (i === index ? value : v))

  const toggleOption = (qi: number, label: string) => {
    if (locked) return
    const question = questions[qi]!
    const current = selected[qi]!
    if (question.allowMultiple) {
      setSelected(update(selected, qi, current.includes(label) ? current.filter((l) => l !== label) : [...current, label]))
    } else {
      setSelected(update(selected, qi, [label]))
      setOtherOpen(update(otherOpen, qi, false))
    }
  }

  const toggleOther = (qi: number) => {
    if (locked) return
    const opening = !otherOpen[qi]
    setOtherOpen(update(otherOpen, qi, opening))
    if (opening && !questions[qi]!.allowMultiple) setSelected(update(selected, qi, []))
  }

  // Picks plus a typed "Other" answer, per question.
  const picks = questions.map((_, qi) => {
    const other = otherOpen[qi] ? otherText[qi]!.trim() : ''
    return other ? [...selected[qi]!, other] : selected[qi]!
  })
  const complete = picks.every((p) => p.length > 0)

  const handleSubmit = () => {
    if (locked || !complete) return
    setSubmitted(true)
    onSubmit(composeAnswer(questions, picks))
  }

  return (
    <div
      className="mt-3 sm:ml-9 max-w-xl flex flex-col gap-5 rounded-2xl p-4 sm:p-5"
      style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, opacity: locked && !submitted ? 0.7 : 1 }}
    >
      {questions.map((q, qi) => (
        <div key={qi} className="flex flex-col gap-2.5">
          <div>
            <span
              className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide mb-1.5"
              style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
            >
              {q.header}
            </span>
            <p className="text-sm font-medium" style={{ color: t.text }}>{q.question}</p>
            {q.allowMultiple && (
              <p className="text-xs mt-0.5" style={{ color: t.mutedText }}>Select all that apply</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {q.options.map((opt) => {
              const isSelected = selected[qi]!.includes(opt.label)
              return (
                <button
                  key={opt.label}
                  type="button"
                  disabled={locked}
                  aria-pressed={isSelected}
                  onClick={() => toggleOption(qi, opt.label)}
                  className="flex items-start gap-3 text-left rounded-xl px-3.5 py-2.5 transition-colors disabled:cursor-default"
                  style={{
                    backgroundColor: isSelected ? `${t.accent}18` : t.primary,
                    border: `1px solid ${isSelected ? t.accent : t.border}`,
                  }}
                >
                  <span
                    className={`mt-0.5 flex items-center justify-center w-4 h-4 shrink-0 border ${q.allowMultiple ? 'rounded' : 'rounded-full'}`}
                    style={{
                      borderColor: isSelected ? t.accent : t.mutedText,
                      backgroundColor: isSelected ? t.accent : 'transparent',
                    }}
                  >
                    {isSelected && <Check size={11} strokeWidth={3} style={{ color: t.buttonText }} />}
                  </span>
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2 text-sm font-medium" style={{ color: t.text }}>
                      {opt.label}
                      {opt.recommended && (
                        <span
                          className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                          style={{ backgroundColor: `${t.accent}18`, color: t.accent }}
                        >
                          Recommended
                        </span>
                      )}
                    </span>
                    {opt.description && (
                      <span className="block text-xs mt-0.5" style={{ color: t.mutedText }}>{opt.description}</span>
                    )}
                  </span>
                </button>
              )
            })}

            <button
              type="button"
              disabled={locked}
              aria-pressed={otherOpen[qi]}
              onClick={() => toggleOther(qi)}
              className="flex items-center gap-3 text-left rounded-xl px-3.5 py-2.5 transition-colors disabled:cursor-default"
              style={{
                backgroundColor: otherOpen[qi] ? `${t.accent}18` : t.primary,
                border: `1px ${otherOpen[qi] ? 'solid' : 'dashed'} ${otherOpen[qi] ? t.accent : t.border}`,
              }}
            >
              <span className="text-sm font-medium" style={{ color: t.text }}>Other…</span>
            </button>
            {otherOpen[qi] && (
              <input
                type="text"
                autoFocus
                disabled={locked}
                value={otherText[qi]}
                onChange={(e) => setOtherText(update(otherText, qi, e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit() }}
                placeholder="Type your own answer"
                className="rounded-xl px-3.5 py-2.5 text-sm outline-none"
                style={{ backgroundColor: t.inputBackground, border: `1px solid ${t.border}`, color: t.text }}
              />
            )}
          </div>
        </div>
      ))}

      {submitted ? (
        <p className="flex items-center gap-1.5 text-xs font-medium" style={{ color: t.mutedText }}>
          <Check size={13} /> Sent
        </p>
      ) : (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={locked || !complete}
          className="self-start flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: t.accentGradient, color: t.buttonText }}
        >
          <Send size={14} />
          Send
        </button>
      )}
    </div>
  )
}

export default ChoiceCard
